import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Mail, Loader2, Send, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGE_OPTIONS = ['Commercial', 'Phase 3', 'Phase 2', 'Phase 1', 'Clinical Stage', 'IND', 'Pre-Clinical'];
const BATCH_OPTIONS = [250, 400, 1000, 2000];

export const StageCampaignModal = ({ open, onOpenChange }) => {
  const [stages, setStages] = useState(['Commercial', 'Phase 3', 'Phase 2']);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [batchSize, setBatchSize] = useState(400);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [starting, setStarting] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/stage-campaign/template`, { withCredentials: true })
      .then(r => { if (!subject) setSubject(r.data.subject); if (!body) setBody(r.data.body); })
      .catch(() => {});
    axios.get(`${API}/stage-campaign/list`, { withCredentials: true })
      .then(r => {
        const active = (r.data.campaigns || []).find(c => c.pending > 0 || c.running);
        if (active) setCampaign(active);
      }).catch(() => {});
    // Auto-load recipients for the pre-checked stages so "Start campaign"
    // isn't stuck disabled while the user wonders why.
    if (!preview && !loadingPreview) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!campaign?.id || (!campaign.running && campaign.pending === 0)) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/stage-campaign/${campaign.id}`, { withCredentials: true });
        setCampaign(r.data);
        if (!r.data.running && r.data.pending === 0) clearInterval(pollRef.current);
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [campaign?.id, campaign?.running, campaign?.pending]);

  const toggleStage = (s) => {
    setStages(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
    setPreview(null); // counts are stale — require a fresh load for the new selection
  };

  const loadPreview = async () => {
    if (!stages.length) { toast.error('Select at least one stage'); return; }
    setLoadingPreview(true);
    try {
      const r = await axios.post(`${API}/stage-campaign/recipients`, { stages }, { withCredentials: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load recipients');
    } finally { setLoadingPreview(false); }
  };

  const start = async () => {
    if (!preview) { toast.error('Load recipients first'); return; }
    setStarting(true);
    try {
      const r = await axios.post(`${API}/stage-campaign/start`, {
        name: 'JCA-Matrix', stages, subject, body, batch_size: batchSize,
      }, { withCredentials: true });
      toast.success(`Campaign started — sending first ${r.data.first_batch} of ${r.data.total}`);
      setCampaign({ id: r.data.campaign_id, total: r.data.total, sent: 0, failed: 0, pending: r.data.total, running: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start campaign');
    } finally { setStarting(false); }
  };

  const sendNext = async () => {
    try {
      const r = await axios.post(`${API}/stage-campaign/${campaign.id}/send-batch`, { batch_size: batchSize }, { withCredentials: true });
      toast.success(`Sending next batch of up to ${r.data.batch_size || batchSize}`);
      setCampaign(prev => ({ ...prev, running: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start batch');
    }
  };

  const sendTest = async () => {
    if (!stages.length) { toast.error('Select at least one stage'); return; }
    setTestSending(true);
    try {
      const r = await axios.post(`${API}/stage-campaign/test-send`, { stages, subject, body }, { withCredentials: true });
      toast.success(`Test sent to ${r.data.sent_to} — personalized as ${r.data.personalized_as}. Check your inbox.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally { setTestSending(false); }
  };

  const skippedTotal = preview ? Object.values(preview.skipped || {}).reduce((a, v) => a + v.count, 0) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="stage-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4" /> Service Campaign — Email Asset Holders</DialogTitle>
          <DialogDescription>
            One email per company holding assets in the selected stages. Sends are batched via Gmail and tracked separately from BD outreach.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Stage buckets</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              {STAGE_OPTIONS.map(s => (
                <label key={s} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <Checkbox checked={stages.includes(s)} onCheckedChange={() => toggleStage(s)} data-testid={`stage-check-${s.toLowerCase().replace(/ /g, '-')}`} />
                  {s}
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={loadPreview} disabled={loadingPreview} data-testid="load-recipients-btn">
              {loadingPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Users className="w-3.5 h-3.5 mr-1" />}
              Load recipients
            </Button>
            {preview && (
              <span className="text-sm text-slate-600" data-testid="recipients-summary">
                <strong>{preview.total_companies}</strong> companies · {preview.total_assets} assets
                {skippedTotal > 0 && <span className="text-slate-400"> · {skippedTotal} skipped</span>}
              </span>
            )}
          </div>

          {preview && preview.dnc_included?.count > 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="dnc-included-note">
              <span className="font-medium">Includes {preview.dnc_included.count} do-not-contact companies</span> — service campaigns override the DNC flag (BD/partnering outreach still respects it).
              <span className="text-amber-600"> {preview.dnc_included.sample.slice(0, 4).join(', ')}{preview.dnc_included.count > 4 ? '…' : ''}</span>
            </div>
          )}

          {preview && skippedTotal > 0 && (
            <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2 space-y-0.5" data-testid="skipped-breakdown">
              {Object.entries(preview.skipped).filter(([, v]) => v.count > 0).map(([k, v]) => (
                <div key={k}><span className="font-medium">{k.replace(/_/g, ' ')}</span>: {v.count} ({v.sample.slice(0, 4).join(', ')}{v.count > 4 ? '…' : ''})</div>
              ))}
            </div>
          )}

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subject</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} className="mt-1" data-testid="campaign-subject-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email body</Label>
            <p className="text-[10px] text-slate-400 mb-1">Placeholders: [Name] [Company] [Assets] [AssetCount] [Stage]</p>
            <Textarea value={body} onChange={e => setBody(e.target.value)} rows={12} className="text-sm" data-testid="campaign-body-textarea" />
          </div>

          <div className="flex items-center gap-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Batch size</Label>
            {BATCH_OPTIONS.map(b => (
              <button key={b} onClick={() => setBatchSize(b)}
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${batchSize === b ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                data-testid={`batch-size-${b}`}>{b}</button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={sendTest}
              disabled={testSending || !subject || !body}
              className="ml-auto h-7 text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
              data-testid="send-test-btn"
            >
              {testSending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
              Send test to me
            </Button>
          </div>
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
            Google Workspace allows up to 2,000 sends/day, but blasting the full list in one day risks spam-flagging your domain. 250–500/day is the safe cruise speed — the campaign remembers progress, so “Send next batch” resumes any time.
          </p>

          {!campaign && (
            <Button onClick={start} disabled={starting || !preview} className="w-full" data-testid="start-campaign-btn">
              {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {preview
                ? `Start campaign — send first ${Math.min(batchSize, preview.total_companies || batchSize)}`
                : loadingPreview
                  ? 'Loading recipients…'
                  : 'Click "Load recipients" above to enable sending'}
            </Button>
          )}

          {campaign && (
            <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="campaign-progress-card">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold">Campaign {campaign.name || ''} · {campaign.id}</span>
                {campaign.running
                  ? <Badge className="bg-blue-100 text-blue-700 animate-pulse">Sending…</Badge>
                  : campaign.pending > 0
                    ? <Badge className="bg-amber-100 text-amber-700">Paused — {campaign.pending} remaining</Badge>
                    : <Badge className="bg-emerald-100 text-emerald-700">Complete</Badge>}
              </div>
              <Progress value={campaign.total ? ((campaign.sent + campaign.failed) / campaign.total) * 100 : 0} />
              <div className="text-xs text-slate-600" data-testid="campaign-progress-text">
                {campaign.sent} sent · {campaign.failed} failed · {campaign.pending} pending · {campaign.total} total
              </div>
              {!campaign.running && campaign.pending > 0 && (
                <Button size="sm" onClick={sendNext} data-testid="send-next-batch-btn">
                  <Send className="w-3.5 h-3.5 mr-1" /> Send next {Math.min(batchSize, campaign.pending)}
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StageCampaignModal;
